import { useState } from 'react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

// FAQ content rebuilt for SEO. Each question targets a real high-volume
// search query around GCSE revision intent. Categorisation kept for UX,
// but the JSON-LD FAQPage schema flattens all questions for Google.
const faqs = [
  {
    category: "Improving GCSE grades",
    questions: [
      {
        q: "How do I improve my GCSE grades?",
        a: "The fastest way to improve GCSE grades is the AI-marked feedback loop: do a topic, get marked against the AQA mark scheme, see WHY each mark was awarded or lost, then re-attempt the same skill. Grade9Ready automates this. Average users see a 1-2 grade lift across an academic year when they hit 30+ marked answers per subject. Combine it with an active revision technique (active recall, spaced repetition) and a printable revision plan, and you have a system not just a hope."
      },
      {
        q: "What is the best way to revise for GCSEs?",
        a: "The best evidence-backed revision techniques are: 1) ACTIVE RECALL — close the book and write what you remember; 2) SPACED REPETITION — re-test topics at 1-day, 3-day, 1-week intervals; 3) PAST PAPER PRACTICE under timed conditions, then mark against the official scheme; 4) ELABORATIVE INTERROGATION — ask 'why' for every fact. Re-reading and highlighting are the LEAST effective. Build a printable plan with our free Revision Planner that schedules these techniques into every session."
      },
      {
        q: "How many hours a day should I revise for GCSEs?",
        a: "Quality matters more than quantity. The sweet spot for most Year 11 pupils is 2-3 focused hours per day across 5-6 days a week, with one full rest day. Use the Pomodoro method: 25 minutes of revision, 5 minutes break, repeat. Hitting 6+ hours daily without rest leads to burnout and worse retention. Diagnostic results matter more than time — track your accuracy on past papers, not your hours."
      },
      {
        q: "What grade is a Grade 9?",
        a: "Grade 9 is the highest possible GCSE grade in the 9-1 numbered system. It maps roughly to the top portion of the old A* — only the top 4-5% of Maths/English entrants achieve a 9 each year. Grade 7 = old A; Grade 8 = old A* low end; Grade 9 = stronger A* / top performance. To target a 9, focus on AO3-style evaluation in essays and the highest-tariff (5+ mark) questions in Maths and Science."
      }
    ]
  },
  {
    category: "Subject-specific revision",
    questions: [
      {
        q: "How do I revise for GCSE Maths?",
        a: "Three pillars: 1) METHOD — write the formula or multiplier on its own line first (free method mark even if arithmetic slips); 2) PRACTICE the high-frequency topics first — quadratics, percentages, simultaneous equations, trig (SOHCAHTOA), tree diagrams; 3) PAST PAPERS under timed conditions. Use our Examiner Tip cheat sheet for Maths to see the specific Grade-9 move per topic. Foundation tier targets grades 1-5; Higher targets 4-9 — make sure you're prepping for the right tier."
      },
      {
        q: "How do I revise for GCSE English Literature?",
        a: "Memorise short embedded quotations (3-6 words), trace character ARCS not snapshots, and ALWAYS foreground the writer ('Shakespeare presents Macbeth as…' beats 'Macbeth feels…'). For AQA: Macbeth, A Christmas Carol, An Inspector Calls and the Power & Conflict anthology each need 3-4 thesis lines you can adapt. Use our English Examiner Tips cheat sheet for the exact Grade-9 phrasing examiners reward."
      },
      {
        q: "How do I revise for GCSE Science?",
        a: "Three things: 1) MASTER THE 13 REQUIRED PRACTICALS — they account for around 15% of marks across Combined Science and crop up every paper; 2) MEMORISE the equations sheet (especially F = ma, V = IR, KE = ½mv²); 3) PRACTICE the 6-mark long-answer questions where most marks are won or lost. Foundation pupils target grades 1-5, Higher 4-9; Triple Science adds extra topics flagged with a Higher badge."
      },
      {
        q: "How do I revise for GCSE English Language?",
        a: "Paper 1 (Fiction) and Paper 2 (Non-Fiction) each have 4 reading questions + a long writing task. Drill Q3 (structure) and Q4 (evaluation) — these are where most pupils lose marks. Use technique LABELS in every paragraph (anaphora, asyndetic listing, sibilance) plus EFFECT on reader. For writing: pick ONE central image and sustain it across the response — examiners reward this in Level 4."
      }
    ]
  },
  {
    category: "About Grade9Ready",
    questions: [
      {
        q: "What is Grade9Ready?",
        a: "Grade9Ready is a UK GCSE revision platform built specifically for Year 10 and Year 11 pupils targeting Grade 9. We cover Maths, English Language, English Literature, and Combined Science (Biology, Chemistry, Physics) — all aligned to the AQA specification. Every topic includes a tutorial, worked examples, adaptive practice, AI-marked exam questions, and an Examiner Brain block with the specific moves that separate Grade 6 from Grade 9."
      },
      {
        q: "Is Grade9Ready free?",
        a: "Yes — start with a free trial that gives Section 1 access in Maths, English, Science and Geography, plus AI marking credits. Paid plans start at £4.99/month. We also offer permanently free resources: the GCSE Revision Planner, Grade Boundaries Calculator, three Examiner Tip Cheat Sheets, and a free sample lesson — all accessible without signup."
      },
      {
        q: "How does the AI marking work?",
        a: "Submit a worded answer (an essay paragraph, a method, a 6-mark Science response). Our AI marks it against the AQA mark scheme and returns: marks awarded out of total, target grade band, what went well (WWW), even better if (EBI), missed mark-scheme points, and an examiner-style summary. It also classifies the type of mistake — calculation slip, misreading, missing AO3, missing keyword — so you can track your error pattern over time and target the right fix."
      },
      {
        q: "Can my parents track my progress?",
        a: "Yes. Parents get their own dashboard showing: topic-by-topic accuracy, overall GCSE compliance score, weakest topics flagged, weekly digest emails, and a Mistake Report email summarising where marks were lost each week. Parents can be linked to one or multiple children, and the number of pupil accounts available depends on the parent subscription plan."
      },
      {
        q: "Which exam boards do you cover?",
        a: "Primarily AQA. Most content transfers cleanly to Edexcel and OCR because the underlying GCSE specifications overlap heavily — set texts (Macbeth, ACC, Inspector Calls, Power & Conflict) are common across boards, and Maths topics are spec-agnostic. Mark schemes and exam questions follow the AQA style."
      },
      {
        q: "Which year groups does Grade9Ready cover?",
        a: "Year 10 and Year 11 pupils sitting GCSEs are our primary audience, with Foundation and Higher tier content for both. KS3 Year 7-9 content is also available for early starters who want a head start on GCSE topics."
      }
    ]
  },
  {
    category: "Account & billing",
    questions: [
      {
        q: "How do I cancel my subscription?",
        a: "You can cancel anytime from the Manage Billing link on your dashboard. You'll keep access until the end of the current billing period. No hidden fees, no questions asked."
      },
      {
        q: "How many pupils can I add to a parent plan?",
        a: "The number of pupil accounts available depends on the parent subscription. Single-pupil plans include one child, while family plans can include multiple children under one account. We keep each plan clear on the pricing page so you can choose the right option for your household."
      },
      {
        q: "What payment methods do you accept?",
        a: "All major credit and debit cards via Stripe (which is our LIVE payment processor). Annual plans are also available with additional discount."
      }
    ]
  }
];

// Flatten for FAQPage JSON-LD — Google reads this and can show your
// questions as rich-result accordions in search results.
const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.flatMap((cat) =>
    cat.questions.map((qa) => ({
      '@type': 'Question',
      name: qa.q,
      acceptedAnswer: { '@type': 'Answer', text: qa.a },
    }))
  ),
};

export default function FAQ() {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen py-12" data-testid="faq-page">
      <SEO
        title="GCSE Revision FAQ — How to Improve Your Grades & Best Ways to Revise"
        description="How do I improve my GCSE grades? What is the best way to revise? How do I revise for GCSE Maths, English and Science? Answers from Grade9Ready, the AI-marked revision platform built for Grade 9."
        path="/faq"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black mb-4" data-testid="faq-h1">
            GCSE Revision — Frequently Asked Questions
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            How to improve your grades, the best ways to revise, subject-specific guides and the practical stuff about Grade9Ready.
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {faqs.map((category, catIndex) => (
            <div key={catIndex} data-testid={`faq-category-${catIndex}`}>
              <h2 className="font-heading font-bold text-xl mb-4">{category.category}</h2>
              <div className="space-y-3">
                {category.questions.map((faq, qIndex) => {
                  const isOpen = openItems[`${catIndex}-${qIndex}`];
                  return (
                    <div key={qIndex} className="neo-card !p-0 overflow-hidden">
                      <button
                        onClick={() => toggleItem(catIndex, qIndex)}
                        className="w-full flex items-center justify-between p-4 text-left"
                        data-testid={`faq-${catIndex}-${qIndex}`}
                      >
                        <span className="font-bold pr-4">{faq.q}</span>
                        {isOpen ? <CaretUp size={24} /> : <CaretDown size={24} />}
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 text-muted-foreground border-t border-foreground/20 pt-4">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <motion.div 
          className="mt-12 neo-card text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="font-heading font-bold text-xl mb-2">Still have questions?</h2>
          <p className="text-muted-foreground mb-4">
            We're here to help! Reach out to our support team.
          </p>
          <a href="/contact" className="neo-btn inline-block">
            Contact Us
          </a>
        </motion.div>
      </div>
    </div>
  );
}
